import { addDays, type ISODate } from './date';

/**
 * Person master data helpers (04-datenmodell "Personen"). `name` stays the display name every screen, statement and
 * export uses; the structured fields (salutation, first/last name) are optional and, when given, `name` is derived
 * from them. Old persons with only `name` keep working unchanged (no migration).
 */
export const SALUTATIONS = ['mr', 'ms', 'diverse', 'company'] as const;
export type Salutation = (typeof SALUTATIONS)[number];

export type PostalAddress = { street?: string; zip?: string; city?: string; country?: string };

/** "Max Müller" from first + last name; empty string when both are empty. */
export function composePersonName(p: { firstName?: string | null; lastName?: string | null }): string {
  return [p.firstName, p.lastName]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * Suggestion for the structured fields of a person that only has `name` (form prefill, never stored silently):
 * the last word is the last name. Joined again with composePersonName it gives the same name back.
 */
export function splitPersonName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: '', lastName: parts[0] ?? '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1]! };
}

/** "Hauptstr. 5, 44135 Dortmund" (empty parts left out). */
export function postalAddressLine(a: PostalAddress | null | undefined): string {
  if (!a) return '';
  const place = [a.zip, a.city]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return [(a.street ?? '').trim(), place].filter(Boolean).join(', ');
}

/** True when the address has no visible content. */
export const isEmptyAddress = (a: PostalAddress | null | undefined) => !postalAddressLine(a);

// ───────────── IBANs of a person (payment matching) ─────────────

/** IBAN without spaces, upper case (the form payment matching compares). */
export const normalizeIban = (s: string) => s.replace(/\s+/g, '').toUpperCase();

/** Shape and ISO 13616 check digits (mod 97); spaces and case do not matter. */
export function isValidIban(s: string): boolean {
  const iban = normalizeIban(s);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  if (iban.startsWith('DE') && iban.length !== 22) return false;
  const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  let rest = 0;
  for (const d of digits) rest = (rest * 10 + Number(d)) % 97;
  return rest === 1;
}

/** "DE89 3704 0044 0532 0130 00" (groups of four). */
export const formatIban = (s: string) => normalizeIban(s).replace(/(.{4})(?=.)/g, '$1 ');

/** IBANs typed into one field: separated by comma, semicolon or line break; spaces inside an IBAN are fine. */
export const parseIbanList = (text: string): string[] => text.split(/[,;\n]/).map(normalizeIban).filter(Boolean);

/** Lower case without diacritics: "Müller" and "muller" match, "Ş" matches "s". */
export function foldText(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').replace(/ß/g, 'ss').toLowerCase();
}

/** Searchable text pieces of a person for the picker (name, contact, addresses, residences). */
export type PersonSearchFields = {
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  currentAddress?: PostalAddress | null;
  previousAddress?: PostalAddress | null;
  extra?: string[];
};

/**
 * Picker search: every word of the query must occur somewhere (name, email, phone, address, residence labels),
 * case and accent insensitive. Phone numbers also match by digits only ("0171 12" finds "+49 171 1234").
 */
export function personMatches(p: PersonSearchFields, query: string): boolean {
  const words = foldText(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const hay = foldText(
    [
      p.name,
      p.firstName,
      p.lastName,
      p.email,
      p.phone,
      p.mobile,
      postalAddressLine(p.currentAddress),
      postalAddressLine(p.previousAddress),
      ...(p.extra ?? []),
    ]
      .filter(Boolean)
      .join(' \u0001 '),
  );
  const digits = [p.phone, p.mobile].map((s) => (s ?? '').replace(/\D/g, '')).filter(Boolean);
  return words.every((w) => {
    if (hay.includes(w)) return true;
    const d = w.replace(/\D/g, '').replace(/^0+/, '');
    return d.length >= 3 && w.replace(/[\d\s+()/-]/g, '') === '' && digits.some((x) => x.includes(d));
  });
}

// ───────────── tenancies of one unit ─────────────

export type TenancyRange = { start: ISODate; end: ISODate | null };

/**
 * Invariant 2 (04-datenmodell): tenancies of one unit never overlap; an open-ended tenancy blocks every later
 * start. Returns the first existing tenancy (by start) that collides with [start, end]. Same rule as the server.
 */
export function findTenancyOverlap<T extends TenancyRange>(
  existing: readonly T[],
  next: TenancyRange,
): T | undefined {
  const to = next.end ?? '9999-12-31';
  return [...existing]
    .sort((a, b) => a.start.localeCompare(b.start))
    .find((t) => t.start <= to && (t.end === null || t.end >= next.start));
}

/**
 * Suggested start of a new tenancy: the day after the last tenancy ended. Null when the latest tenancy is still
 * open-ended (record the move-out first) and undefined when the unit never had a tenancy.
 */
export function suggestTenancyStart(existing: readonly TenancyRange[]): ISODate | null | undefined {
  if (!existing.length) return undefined;
  if (existing.some((t) => t.end === null)) return null;
  const last = existing.reduce((m, t) => (t.end! > m ? t.end! : m), existing[0]!.end!);
  return addDays(last, 1);
}

/** Why a tenancy cannot be deleted (GET /tenancies/:id/deletion, CONFLICT details of DELETE). */
export const TENANCY_DELETE_BLOCKERS = [
  'events',
  'ledger',
  'statements',
  'readings',
  'messages',
  'invites',
  'bank',
  'dunning',
  'metering',
  'deposit',
  'documents',
] as const;
export type TenancyDeleteBlocker = (typeof TENANCY_DELETE_BLOCKERS)[number];

/**
 * Why a tenancy cannot be moved to another unit (POST /tenancies/:id/move, a correction of a wrong unit):
 * a finalised statement already billed it, meter readings or heating results belong to the old unit's meters,
 * a rent change letter names the old unit. Into another property also bookings (they belong to the property's
 * books, append-only) and contract documents (filed in the old property) block it.
 */
export const TENANCY_MOVE_BLOCKERS = ['statements', 'readings', 'metering', 'rentChanges', 'ledger', 'documents', 'handovers'] as const;
export type TenancyMoveBlocker = (typeof TENANCY_MOVE_BLOCKERS)[number];
